import React from 'react';
import { useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DSBox from '../../../design-system/DSBox';
import DSModal from '../../../design-system/DSModal';
import DSCircularProgress from '../../../design-system/DSCircularProgress';
import { DSTypography } from '../../../design-system/Typography';
import KlydoPlayer from '../../../components/KlydoPlayer/KlydoPlayer';
import usePublicKlydoQuery from '../../PublicKlydo/hooks/usePublicKlydoQuery';
import useResponsiveClockScale from '../../PublicKlydo/hooks/useResponsiveClockScale';

type GuideKlydoModalProps = {
  klydoId: string | null;
  open: boolean;
  onClose: () => void;
};

// Popup that previews a single klydo (player only) when a guide body link
// points at the public klydo route. Fetches via the public, no-auth endpoint
// so any klydo an admin links resolves.
const GuideKlydoModal = ({ klydoId, open, onClose }: GuideKlydoModalProps) => {
  const { palette, spacing } = useTheme();
  const responsiveScale = useResponsiveClockScale();
  const { data: klydo, isLoading, error } = usePublicKlydoQuery(klydoId || '');

  // Keep the preview comfortably smaller than the full-page share view.
  const scale = responsiveScale * 0.75;

  const renderBody = () => {
    if (isLoading) {
      return <DSCircularProgress />;
    }
    if (error || !klydo) {
      return (
        <DSTypography sx={{ color: palette.text.primary, textAlign: 'center' }}>
          {error?.response?.data?.message ||
            error?.message ||
            'Klydo not found'}
        </DSTypography>
      );
    }
    return (
      <KlydoPlayer
        klydo={klydo}
        scale={scale}
        dialsHidden
        muted
        pendulumLogo={klydo.pendulumLogo || undefined}
      />
    );
  };

  return (
    <DSModal open={open} onClose={onClose}>
      <DSBox
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 220,
          minHeight: 220,
          padding: spacing(5, 3, 3),
          overflowX: 'hidden',
        }}
      >
        <DSBox
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: spacing(1),
            right: spacing(1),
            width: 34,
            height: 34,
            borderRadius: '50%',
            backgroundColor: palette.white[5],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: palette.text.secondary,
            zIndex: 1,
            '&:hover': { backgroundColor: palette.white[10] },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </DSBox>
        {renderBody()}
      </DSBox>
    </DSModal>
  );
};

export default GuideKlydoModal;
